'use strict';

const QUIZ_COOKIE_NAME = 'skin_quiz_completed';

/**
 * Check if the skin quiz completion cookie is valid
 * @returns {boolean} True if the cookie exists and is valid
 */
const isQuizCookieValid = () => {
  return document.cookie.split(';').some(c => c.trim().startsWith(`${QUIZ_COOKIE_NAME}=`));
};

const fetchRequest = async (url, method, body = null) => {
    try {
        const options = {
            method,
            headers: {
                'Content-Type': 'application/json',
            },
        };

        if (body) options.body = JSON.stringify(body);

        const response = await fetch(url, options);

        if (!response.ok) {
            const errorMessage = `Error: ${response.status} - ${response.statusText}`;
            throw new Error(errorMessage);
        }

        return await response.json();
    } catch (error) {
        console.error(`Request failed for ${method} ${url}:`, error);
        throw error;
    }
};

/**
 * Fetch the current state of the cart
 * @returns {Promise<Object>} Resolves with the current cart state
 */
const getCart = () => fetchRequest('/cart.js', 'GET');

/**
 * Clear all items from the cart and specialist attributes
 * @returns {Promise<Object>} Resolves with the current cart state
 */
const clearCart = async () => {
  // First clear all items from the cart
  const response = await fetchRequest('/cart/clear.js', 'POST');
  
  // Then clear specialist attributes if they exist
  try {
    await fetchRequest('/cart/update.js', 'POST', {
      attributes: {
        'Specialist Id': '',
        'Specialist Name': '',
        'Salon': '',
        'State': '',
        'City': ''
      }
    });
  } catch (error) {
    console.error('Error clearing specialist attributes:', error);
  }
  
  return response;
};

/**
 * Add an item to the cart
 * @param {Object} item - Must include `id` and `quantity`. May include `properties` for line item attributes.
 * @param {Object} [cartAttributes] - Optional cart-level attributes (e.g., { cart_started_as: 'B2C' })
 * @returns {Promise<Object>} Resolves with the updated cart state
 */
const addToCart = async (item, cartAttributes = {}) => {
  if (!item || typeof item.id === 'undefined' || typeof item.quantity !== 'number') {
    throw new Error('Invalid item object. Must include `id` and `quantity`.');
  }
  
  // Get existing cart to preserve attributes
  const cart = await getCart();
  const existingAttributes = cart.attributes || {};

  // Merge: existing attributes + passed attributes + quiz attribute if cookie valid
  let mergedAttributes = { ...existingAttributes, ...cartAttributes };

  if (isQuizCookieValid()) {
    mergedAttributes.skin_quiz_converted = '1';
  }

  const payload = {
    items: [item]
  };

  if (Object.keys(mergedAttributes).length > 0) {
    payload.attributes = mergedAttributes;
  }

  return fetchRequest('/cart/add.js', 'POST', payload);
};

/**
 * Update or remove an item in the cart
 * @param {Object} change - The change object (must include `id` and `quantity`)
 * @returns {Promise<Object>} Resolves with the updated cart state
 * @throws {Error} If the input is invalid
 */
const updateCart = (change) => {
    if (!change || typeof change.id === 'undefined' || typeof change.quantity !== 'number') {
        throw new Error('Invalid change object. Must include `id` and `quantity`.');
    }
    return fetchRequest('/cart/change.js', 'POST', change);
};

/**
 * Remove an item from the cart
 * @param {string} id - The ID of the item to remove
 * @returns {Promise<Object>} Resolves with the updated cart state
 */
const removeFromCart = (id) => {
    if (!id) {
        throw new Error('Invalid ID. Must provide an `id` to remove an item.');
    }
    return updateCart({ id, quantity: 0 });
};

export { getCart, clearCart, addToCart, updateCart, removeFromCart, isQuizCookieValid };
